import { useQuery } from "../../Utils/query";
import {
  checkLandingPage,
  getFeedControls,
  getUserAndCheckExistence,
} from "../HelperFunction/HelperFunction"; // <- new helper
import { SupabaseClient } from "@supabase/supabase-js";
import { supabase, supabasePublic } from "../../config/supabaseClient";
import { queryClient } from "../query/queryClient";
import { isAbortError } from "../errors/isAbortError";

export function profileSetupQueryKey(username: string, page: string) {
  return ["fetchProfileSetup", username, page] as const;
}

export async function fetchProfileSetupForQuery(params: {
  username: string;
  page: string;
  client?: SupabaseClient;
  signal?: AbortSignal;
}) {
  const { username, page, client = supabase, signal } = params;
  try {
    const userPromise = getUserAndCheckExistence(username, client, signal);
    // Public `/website` bootstrap: resolve user first; feed controls load separately.
    const feedPromise =
      page === "website"
        ? Promise.resolve({})
        : getFeedControls(page, username, client, signal);

    const [userData, feedControls]: any = await Promise.allSettled([
      userPromise,
      feedPromise,
    ]);

    if (userData.status === "rejected" && isAbortError(userData.reason)) {
      throw userData.reason;
    }
    if (feedControls.status === "rejected" && isAbortError(feedControls.reason)) {
      throw feedControls.reason;
    }

    if (userData.status === "rejected") {
      throw userData.reason;
    }

    const userDataResult =
      userData.status === "fulfilled" ? userData.value : null;
    const feedControlsResult =
      feedControls.status === "fulfilled" ? feedControls.value : null;

    const feedControlsSanitized =
      feedControlsResult != null &&
      typeof feedControlsResult === "object" &&
      !Array.isArray(feedControlsResult)
        ? feedControlsResult
        : {};

    if (!userDataResult || !userDataResult.user_id) {
      return { userNotFound: true, combinedRes: null };
    }

    if (userDataResult.user_id) {
      checkLandingPage(userDataResult.user_id).catch((error) => {
        console.error("Error checking landing page:", error);
      });
    }

    const {
      instagram_links,
      userExists,
      description,
      search_bar_text,
      is_embed_search,
      active_campaign,
    } = userDataResult;

    const combinedRes = {
      userData: userDataResult,
      feedControls: feedControlsSanitized,
      userExists,
      is_embed_search,
      instagram_links,
      description,
      search_bar_text,
      active_campaign,
    };

    return { combinedRes, userNotFound: false };
  } catch (error: any) {
    if (isAbortError(error)) {
      throw error;
    }
    console.error("Error in useLoadProfileSetup:", error);
    throw error;
  }
}

/** Start the LiB profile row as soon as the username is known (route render). */
export function prefetchProfileSetup(username: string) {
  const u = username.trim().toLowerCase();
  if (!u) return;
  void queryClient.prefetchQuery(
    profileSetupQueryKey(u, "preview"),
    () =>
      fetchProfileSetupForQuery({
        username: u,
        page: "preview",
        client: supabasePublic,
      }),
    { staleTime: 5 * 60 * 1000 },
  );
}

export function useLoadProfileSetup(
  username: any,
  page: any,
  queryOptions: any = {},
  client: SupabaseClient = supabase
) {
  async function fetchAllData({ signal }: { signal?: AbortSignal }) {
    return fetchProfileSetupForQuery({
      username,
      page,
      client,
      signal,
    });
  }

  // NOTE: do not include queryOptions in the key; it is often a new object
  // each render, which causes churn/cancellations across tabs.
  return useQuery(profileSetupQueryKey(username, page), fetchAllData, {
    enabled: Boolean(username) && Boolean(page) && Boolean(client),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    ...queryOptions,
  });
}
